import { Component, Input, SimpleChanges } from '@angular/core';
import { RangeDatePicker } from 'app/model/rangeDatePicker';
import { ngbDateToDate } from 'app/shared/utils/dateToNgbDate';
import { DashboardCalendarDonutStatsService } from 'app/shared/services/data/dashboard/dashboard-calendar-donut-stats.service';
import { DashboardCalendarChartStats, DashboardCalendarCount, DashboardCalendarPencentage } from 'app/model/dashboardCalendar';
import { CalendarStatus } from 'app/model/calendar';
import { Chart } from 'app/model/chart';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-dashboard-calendar-donut-stats',
  templateUrl: './dashboard-calendar-donut-stats.component.html',
  styleUrls: ['./dashboard-calendar-donut-stats.component.scss']
})
export class DashboardCalendarDonutStatsComponent {
  private data: any

  public isLoading: boolean = true

  public chart: Chart

  public fromDate: Date | null
  public toDate: Date | null

  public totals: DashboardCalendarCount
  public percentages: DashboardCalendarPencentage

  public donutStats: DashboardCalendarChartStats

  public isAccepted: boolean
  public isEmpty: boolean

  @Input() public status: CalendarStatus
  @Input() public selectedDptId: number
  @Input() public selectedEmployeeId: number

  @Input() public dateRange: RangeDatePicker

  /**
  * Constructor
  *
  * @param {DashboardCalendarDonutStatsService} dashboardCalendarDonutStatsService
  * 
  */
  constructor(
    private dashboardCalendarDonutStatsService: DashboardCalendarDonutStatsService
  ) { }

  ngOnInit(): void {
    this.initializeValues()
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dateRange = changes['dateRange']
    const status = changes['status']

    if (dateRange && dateRange.currentValue) this.checkIfRangeIsValid(dateRange.currentValue)
    if (status && status.currentValue) this.checkIfItsAccepted(status.currentValue)

    this.getDashboardCalendarStats()
  }

  private initializeValues(): void {
    this.data = []

    this.fromDate = null
    this.toDate = null

    this.totals = new DashboardCalendarCount()
    this.percentages = new DashboardCalendarPencentage()

    this.donutStats = new DashboardCalendarChartStats()

    this.isAccepted = true
    this.isEmpty = true
  }

  private checkIfRangeIsValid(dateRange: RangeDatePicker): void {
    this.fromDate = ngbDateToDate(dateRange.fromDate)
    this.toDate = ngbDateToDate(dateRange.toDate)
  }

  private checkIfItsAccepted(status: CalendarStatus): void {
    this.isAccepted = status === CalendarStatus.ACCEPTED
  }

  private getDashboardCalendarStats(): void {
    this.isLoading = true

    this.dashboardCalendarDonutStatsService.getDashboardCalendarStats(
      this.selectedDptId,
      this.selectedEmployeeId,
      this.fromDate,
      this.toDate
    )
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (data) => {
          this.totals = data.totals
          this.percentages = data.percentages

          this.mapDonutStats()

          this.mapData()

          this.checkIfItsEmpty()

          this.chart = null

          setTimeout(() => {
            this.createChart()
          }, 100)
        },
        error: () => {
          const alertOptions: SweetAlertOptions = {
            title: '¡Error!',
            html: `Ha ocurrido un error al intentar cargar el gráfico de los eventos. <br><br> Póngase en contacto con un administrador.`,
            icon: 'error',
            customClass: {
              confirmButton: 'btn red-bg text-white ml-1'
            },
          }

          this.fireSwalAlert(alertOptions)
        }
      })
  }

  private mapDonutStats(): void {
    this.donutStats = this.dashboardCalendarDonutStatsService.mapDonutStats(this.totals, this.percentages, this.isAccepted)
  }

  private mapData(): void {
    this.data = this.dashboardCalendarDonutStatsService.mapData(this.donutStats.totals)
  }

  private checkIfItsEmpty(): void {
    const totals = this.totals

    this.isEmpty = totals.total_accepted === 0 && totals.total_pending === 0
  }

  private createChart(): void {
    this.chart = this.dashboardCalendarDonutStatsService.createChart(this.data, this.totals, this.isAccepted)
  }

  private fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return this.dashboardCalendarDonutStatsService.fireSwalAlert(options)
  }
}
